import React, { useState } from "react";
import { useForm } from "react-hook-form";
import api from "../../utils/axios.config";
import { toast } from "react-hot-toast";
import Loading from "../Shared/Loading";
import {
  Image,
  User,
  Mail,
  Smartphone,
  Home,
  Briefcase,
  Facebook,
  MessageSquare,
  Users,
  UserCheck,
} from "lucide-react";

function AddMember() {
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm();

  const committeeOptions = [
    {
      name: "কেন্দ্রীয়",
      value: "central",
    },
    {
      name: "কার্যকরী পরিষদ",
      value: "executive",
    },
    {
      name: "কক্সবাজার",
      value: "cox-bazar",
    },
    {
      name: "নোয়াখালী-লক্ষ্মীপুর",
      value: "noakhali-lakshmipur",
    },
  ];

  const organizationDesignationOptions = [
    { value: "member", label: "সদস্য" },
    { value: "advisor", label: "উপদেষ্টা" },
  ];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async ({
    designation,
    occupation,
    address,
    file,
    mobile,
    name,
    email,
    facebookUrl,
    whatsappUrl,
    organizationDesignation,
    committeeName,
  }) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("image", file[0]);
    formData.append("name", name);
    formData.append("designation", designation);
    formData.append("occupation", occupation || "");
    formData.append("mobile", mobile);
    formData.append("email", email);
    formData.append("facebookUrl", facebookUrl);
    formData.append("whatsappUrl", whatsappUrl);
    formData.append("organizationDesignation", organizationDesignation);
    formData.append("address", address);
    formData.append("committeeName", committeeName);

    try {
      const { data } = await api.post("member/create", formData);
      if (data.success) {
        toast.success(data?.message);
        reset();
        setImagePreview(null);
      } else {
        toast.error(data?.error);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error ?? error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-primary mb-8">
        <Users className="inline mr-2" size={28} />
        কার্যকরী সদস্য যুক্ত করুন
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Image Upload with Preview */}
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Image className="inline mr-2" size={18} />
              ছবি নির্বাচন করুন *
            </label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-100">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image className="text-gray-400" size={32} />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  {...register("file", { required: "ছবি আবশ্যক" })}
                  onChange={handleImageChange}
                  className="file-input file-input-bordered file-input-primary w-full"
                  accept="image/*"
                />
                <p className="mt-1 text-sm text-red-500">
                  {errors.file && errors.file.message}
                </p>
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <User className="mr-2" size={18} />
                নাম *
              </span>
            </label>
            <input
              {...register("name", { required: "নাম আবশ্যক" })}
              type="text"
              placeholder="নাম লিখুন"
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.name && errors.name.message}
              </span>
            </label>
          </div>

          {/* Email */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Mail className="mr-2" size={18} />
                ইমেইল *
              </span>
            </label>
            <input
              {...register("email", { required: "ইমেইল আবশ্যক" })}
              type="email"
              placeholder="ইমেইল লিখুন"
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.email && errors.email.message}
              </span>
            </label>
          </div>


          {/* Designation */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Briefcase className="mr-2" size={18} />
                পদবী *
              </span>
            </label>
            <input
              {...register("designation", {
                required: "পদবী আবশ্যক",
              })}
              type="text"
              placeholder="পদবী লিখুন"
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.designation && errors.designation.message}
              </span>
            </label>
          </div>

          {/* Occupation */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <UserCheck className="mr-2" size={18} />
                পেশা
              </span>
            </label>
            <input
              {...register("occupation")}
              type="text"
              placeholder="পেশা লিখুন"
              className="input input-bordered w-full"
            />
          </div>

          {/* Mobile */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Smartphone className="mr-2" size={18} />
                মোবাইল নাম্বার *
              </span>
            </label>
            <input
              {...register("mobile", { required: "মোবাইল নাম্বার আবশ্যক" })}
              type="tel"
              placeholder="মোবাইল নাম্বার লিখুন"
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.mobile && errors.mobile.message}
              </span>
            </label>
          </div>

          {/* Address */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Home className="mr-2" size={18} />
                ঠিকানা *
              </span>
            </label>
            <input
              {...register("address", { required: "ঠিকানা আবশ্যক" })}
              type="text"
              placeholder="ঠিকানা লিখুন"
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.address && errors.address.message}
              </span>
            </label>
          </div>

          {/* Facebook URL */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Facebook className="mr-2" size={18} />
                ফেসবুক প্রোফাইল লিংক
              </span>
            </label>
            <input
              {...register("facebookUrl")}
              type="url"
              placeholder="ফেসবুক প্রোফাইল লিংক লিখুন"
              className="input input-bordered w-full"
            />
          </div>

          {/* WhatsApp URL */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <MessageSquare className="mr-2" size={18} />
                হোয়াটসএ্যাপ প্রোফাইল লিংক
              </span>
            </label>
            <input
              {...register("whatsappUrl")}
              type="url"
              placeholder="হোয়াটসএ্যাপ প্রোফাইল লিংক লিখুন"
              className="input input-bordered w-full"
            />
          </div>

          {/* Organization Designation */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Users className="mr-2" size={18} />
                সংগঠনের পদবী *
              </span>
            </label>
            <select
              {...register("organizationDesignation", {
                required: "সংগঠনের পদবী নির্বাচন করুন",
              })}
              className="select select-bordered w-full"
            >
              <option value="">নির্বাচন করুন</option>
              {organizationDesignationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.organizationDesignation &&
                  errors.organizationDesignation.message}
              </span>
            </label>
          </div>

          {/* Committee Name */}
          <div className="form-control">
            <label className="label">
              <span className="label-text flex items-center">
                <Users className="mr-2" size={18} />
                কমিটি নির্বাচন করুন *
              </span>
            </label>
            <select
              {...register("committeeName", {
                required: "কমিটি নির্বাচন করুন",
              })}
              className="select select-bordered w-full"
            >
              <option value="">কমিটি নির্বাচন করুন</option>
              {committeeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.name} কমিটি
                </option>
              ))}
            </select>
            <label className="label">
              <span className="label-text-alt text-red-500">
                {errors.committeeName && errors.committeeName.message}
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            className="btn btn-primary text-white px-8 py-3 rounded-lg"
            type="submit"
          >
            সদস্য যুক্ত করুন
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddMember;
